/**
 * recordingUploader.js — Sep 2026 · smart upload Nov 2026
 * Resumable uploader: OPFS file → R2 multipart via presigned URLs.
 *  • Live mode (in-class): completed 10MB parts are uploaded one at a time
 *    behind a network-quality gate (LiveKit quality / cellular / screen
 *    share / thermal). Weak network ⇒ pause; in-flight part is aborted.
 *  • Post-class: remaining parts resume from where live mode stopped, with
 *    exponential retry + `online` wake-up. Progress persists in
 *    localStorage so an interrupted upload resumes on the next app load.
 */
import axios from 'axios';
import { fixWebmDuration } from '@fix-webm-duration/fix';
import { opfsDir, listPendingFiles, activeRecordingFiles, withFileLock } from './classRecorder';

const API = process.env.REACT_APP_BACKEND_URL;
const PART = 10 * 1024 * 1024;
// QA override (`localStorage.mesp_rec_part_mb`, min 5 = R2 minimum part) —
// the chosen size is pinned per file so resume never mixes part sizes.
const livePartBytes = () => {
  try {
    const mb = Number(localStorage.getItem('mesp_rec_part_mb'));
    return mb >= 5 && mb <= 100 ? mb * 1024 * 1024 : PART;
  } catch { return PART; }
};
const partOf = (st) => (st && st.partBytes) || PART;
const LS = 'mesp_rec_uploads';
const LIVE_INIT_PARTS = 30;
const MAX_LIVE_QUEUE = 3;          // ≤30MB of ready parts kept in memory while paused
const RETRY_MIN_MS = 10000;
const RETRY_MAX_MS = 300000;

const state = () => { try { return JSON.parse(localStorage.getItem(LS) || '{}'); } catch { return {}; } };
const save = (s) => { try { localStorage.setItem(LS, JSON.stringify(s)); } catch { /* noop */ } };
const ksaDate = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);
const post = (url, body) => axios.post(`${API}${url}`, body, { withCredentials: true });

// ── status bus ───────────────────────────────────────────────────────────
const listeners = new Set();
let lastStatus = { phase: 'idle' };
export const getUploadStatus = () => lastStatus;
export const onUploadProgress = (fn) => { listeners.add(fn); fn(lastStatus); return () => listeners.delete(fn); };
const setStatus = (p) => {
  lastStatus = { at: Date.now(), ...p };
  listeners.forEach((fn) => { try { fn(lastStatus); } catch { /* noop */ } });
};

// ── shared primitives ────────────────────────────────────────────────────
async function ensureStarted(fileName, meta) {
  const all = state();
  let st = all[fileName] || {};
  if (!st.recordingId) {
    const { data } = await post('/api/class-recordings/start', {
      classroom_id: meta.classroomId, client_v2: true,
      session_date: meta.sessionDate || st.sessionDate || ksaDate(),
      replaces: st.replaces || null,
    });
    st = {
      ...st, recordingId: data.recording_id, partIndex: data.part_index, etags: st.etags || {},
      classroomId: meta.classroomId, sessionDate: meta.sessionDate || st.sessionDate || ksaDate(),
      durationSec: meta.durationSec || st.durationSec || null,
    };
    all[fileName] = st; save(all);
  }
  if (!st.partUrls) {
    const { data } = await post(`/api/class-recordings/${st.recordingId}/init-upload`, {
      parts_count: meta.partsCount || LIVE_INIT_PARTS, content_type: 'video/webm',
      size_bytes: meta.sizeBytes || 0,
    });
    st.partUrls = data.part_urls; all[fileName] = st; save(all);
  }
  return st;
}

const patchSt = (fileName, patch) => {
  const all = state();
  all[fileName] = { ...(all[fileName] || {}), ...patch };
  save(all);
  return all[fileName];
};

async function mintUrls(st, fileName, nums) {
  const { data } = await post(`/api/class-recordings/${st.recordingId}/more-part-urls`, { part_numbers: nums });
  const urls = [...(st.partUrls || [])];
  nums.forEach((n) => { urls[n - 1] = data.part_urls[n] || data.part_urls[String(n)]; });
  return patchSt(fileName, { partUrls: urls });
}

async function putPart(fileName, n, blob, signal) {
  let st = state()[fileName];
  if (!st.partUrls?.[n - 1]) st = await mintUrls(st, fileName, [n, n + 1, n + 2, n + 3, n + 4]);
  let resp = await fetch(st.partUrls[n - 1], { method: 'PUT', body: blob, signal });
  if (resp.status === 403) {   // presign expired → re-mint
    st = await mintUrls(st, fileName, [n]);
    resp = await fetch(st.partUrls[n - 1], { method: 'PUT', body: blob, signal });
  }
  if (!resp.ok) throw new Error(`part ${n} failed: ${resp.status}`);
  const etag = (resp.headers.get('ETag') || '').replaceAll('"', '');
  const cur = state()[fileName] || st;
  return patchSt(fileName, { etags: { ...(cur.etags || {}), [n]: etag } });
}

const heartbeat = (st, body) => {
  if (!st?.recordingId) return;
  post(`/api/class-recordings/${st.recordingId}/upload-progress`, body).catch(() => {});
};

// ── live (in-class) controller ───────────────────────────────────────────
/**
 * @param {object} o
 * @param {string} o.fileName   OPFS file being written by ClassRecorder
 * @param {string} o.classroomId
 * @param {() => {allow:boolean, gapSec?:number, reason?:string}} o.gate
 * @param {number} [o.checkEverySec]
 */
export function startLiveUpload({ fileName, classroomId, gate, checkEverySec = 45 }) {
  let stopped = false;
  let inflight = null;
  let lastPartAt = 0;
  let buffer = [];
  let bufBytes = 0;
  let written = 0;
  let nextN = 1;
  let queue = [];           // [{ n, blob }] — ready, not yet uploaded
  let busy = false;
  const P = livePartBytes();
  patchSt(fileName, { classroomId, live: true, sessionDate: ksaDate(), etags: {}, partBytes: P });

  const pushChunk = (blob) => {
    if (stopped) return;
    written += blob.size;
    buffer.push(blob); bufBytes += blob.size;
    while (bufBytes >= P) {
      const big = new Blob(buffer);
      queue.push({ n: nextN, blob: big.slice(0, P) });
      nextN += 1;
      const rest = big.slice(P);
      buffer = rest.size ? [rest] : []; bufBytes = rest.size;
      if (queue.length > MAX_LIVE_QUEUE) queue.shift();   // falls back to post-class upload
    }
    maybeUpload();
  };

  const maybeUpload = async () => {
    if (stopped || busy || !queue.length) return;
    const g = gate();
    const st = state()[fileName] || {};
    const done = Object.keys(st.etags || {}).length;
    if (!g.allow) {
      setStatus({ phase: 'live-paused', reason: g.reason, file: fileName, done, total: nextN - 1 });
      return;
    }
    if (Date.now() - lastPartAt < (g.gapSec || 15) * 1000) return;
    busy = true;
    const item = queue[0];
    try {
      await ensureStarted(fileName, { classroomId, sessionDate: ksaDate() });
      inflight = new AbortController();
      setStatus({ phase: 'live-uploading', file: fileName, done, total: nextN - 1 });
      const after = await putPart(fileName, item.n, item.blob, inflight.signal);
      queue = queue.filter((q) => q.n !== item.n);
      lastPartAt = Date.now();
      const liveParts = Object.keys(after.etags).length;
      patchSt(fileName, { liveParts, liveBytes: liveParts * P });
      heartbeat(after, { parts_done: liveParts, bytes_done: liveParts * P, total_bytes: written, phase: 'live' });
      setStatus({ phase: 'live-uploading', file: fileName, done: liveParts, total: nextN - 1 });
    } catch {
      // aborted (quality drop) or transient failure — retried on next tick;
      // the part stays in the queue (or is re-read from OPFS after class).
      lastPartAt = Date.now();
    } finally {
      inflight = null; busy = false;
    }
  };

  const tick = setInterval(maybeUpload, checkEverySec * 1000);
  // guard: a part in flight is cancelled the moment the network turns weak
  const guard = setInterval(() => { if (inflight && !gate().allow) inflight.abort(); }, 3000);

  return {
    pushChunk,
    stop() {
      stopped = true;
      clearInterval(tick); clearInterval(guard);
      inflight?.abort();
      queue = []; buffer = [];
      setStatus({ phase: 'idle' });
    },
  };
}

// ── post-class / resume uploader ─────────────────────────────────────────
const isWebm = async (file) => {
  try {
    const b = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    return b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3;   // EBML magic
  } catch { return false; }
};

async function uploadOne(fileMeta, meta, depth = 0) {
  const dir = await opfsDir();
  let file = await (await dir.getFileHandle(fileMeta.name)).getFile();
  let st = state()[fileMeta.name] || {};
  const uploadedBefore = Object.keys(st.etags || {}).length;
  if (!uploadedBefore && !(await isWebm(file))) {
    // not a MediaRecorder file (corrupt / foreign) — never publish it
    await dir.removeEntry(fileMeta.name).catch(() => {});
    const all = state(); delete all[fileMeta.name]; save(all);
    return false;
  }

  // MediaRecorder webm has no duration header — patch it before upload when
  // no bytes left yet (streamed-live files are remuxed server-side instead).
  const durSec = meta.durationSec || st.durationSec;
  if (durSec && !uploadedBefore) {
    try { file = await fixWebmDuration(file, Math.round(durSec * 1000), { logger: false }); } catch { /* server fallback */ }
  }
  const P = partOf(st);
  const partsCount = Math.max(1, Math.ceil(file.size / P));
  st = await ensureStarted(fileMeta.name, { ...meta, sizeBytes: file.size, partsCount });
  for (let n = 1; n <= partsCount; n += 1) {
    if (st.etags[n]) continue;
    setStatus({ phase: 'uploading', file: fileMeta.name, done: Object.keys(st.etags).length, total: partsCount });
    st = await putPart(fileMeta.name, n, file.slice((n - 1) * P, Math.min(n * P, file.size)));
    const done = Object.keys(st.etags).length;
    heartbeat(st, { parts_done: done, bytes_done: Math.min(done * P, file.size), total_bytes: file.size, phase: 'post' });
    setStatus({ phase: 'uploading', file: fileMeta.name, done, total: partsCount });
  }
  let res;
  try {
    res = await post(`/api/class-recordings/${st.recordingId}/complete`, {
      parts: Object.entries(st.etags).map(([pn, etag]) => ({ part_number: Number(pn), etag })),
      duration_sec: meta.durationSec || st.durationSec || null, size_bytes: file.size,
      streamed_live: (st.liveParts || 0) > 0, live_parts: st.liveParts || 0, live_bytes: st.liveBytes || 0,
      recorder_stopped_at: meta.stoppedAt || st.stoppedAt || null,
    });
  } catch (e) {
    if (e?.response?.status === 410) {
      // Upload session expired on R2 — restart from scratch next drain.
      const all = state(); delete all[fileMeta.name]; save(all);
      throw new Error('upload session expired — will restart');
    }
    throw e;
  }
  if (res.data?.already && res.data?.partial && depth === 0) {
    // Server published a partial copy (48h rule) while we still hold the
    // full file → upload the full file fresh, replacing the partial one.
    const all = state();
    all[fileMeta.name] = {
      classroomId: st.classroomId, sessionDate: st.sessionDate, durationSec: st.durationSec,
      stoppedAt: st.stoppedAt, replaces: st.recordingId, etags: {},
    };
    save(all);
    return uploadOne(fileMeta, meta, 1);
  }
  await dir.removeEntry(fileMeta.name).catch(() => {});
  const all = state(); delete all[fileMeta.name]; save(all);
  setStatus({ phase: 'done', file: fileMeta.name, done: partsCount, total: partsCount, finished: true });
  return true;
}

let running = false;
let retryTimer = null;
let retryMs = RETRY_MIN_MS;
let wired = false;

const wireWakeups = () => {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  window.addEventListener('online', () => { retryMs = RETRY_MIN_MS; drainPendingUploads(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) drainPendingUploads(); });
  window.addEventListener('beforeunload', (e) => {
    if (lastStatus.phase === 'uploading') { e.preventDefault(); e.returnValue = ''; }
  });
};

const scheduleRetry = (reason) => {
  clearTimeout(retryTimer);
  const ms = retryMs;
  retryMs = Math.min(RETRY_MAX_MS, retryMs * 2);
  setStatus({ ...lastStatus, phase: 'waiting', reason, retryAt: Date.now() + ms });
  retryTimer = setTimeout(() => drainPendingUploads(), ms);
};

/** Drains all pending OPFS recordings sequentially. Safe to call repeatedly. */
export async function drainPendingUploads(metaByFile = {}) {
  if (running) return;
  running = true;
  wireWakeups();
  clearTimeout(retryTimer);
  try {
    const pending = await listPendingFiles();
    const saved = state();
    for (const f of pending) {
      const s = saved[f.name] || {};
      if (activeRecordingFiles.has(f.name)) continue;   // still being recorded in this tab
      const cid = metaByFile[f.name]?.classroomId || s.classroomId
        || (f.name.match(/^rec-([a-f0-9]{24})-/) || [])[1];
      if (!cid) continue;
      try {
        const r = await withFileLock(f.name, (got) => (got
          ? uploadOne(f, { classroomId: cid, ...(metaByFile[f.name] || {}) })
          : 'locked'));
        if (r === 'locked') continue;   // another tab is recording/uploading this file
        retryMs = RETRY_MIN_MS;
      } catch (e) {
        const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
        scheduleRetry(offline ? 'offline' : 'network');
        return;
      }
    }
    if (lastStatus.phase !== 'done') setStatus({ phase: 'idle' });
  } finally { running = false; }
}

/** Marks a recording file as finished so a later drain may upload it. */
export const markRecorderStopped = (fileName, { durationSec }) =>
  patchSt(fileName, { stoppedAt: new Date().toISOString(), durationSec });

export const hasPendingUploads = async () => (await listPendingFiles()).length > 0;
